/**
 * Placement creation/editing form component.
 */

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import type { DomainResponse } from '@/api/schemas';
import './placement-form.scss';

const placementSchema = z.object({
  domain_id: z.string().uuid('Please select a domain'),
  destination_url: z.string().url('Must be a valid URL').refine((url) => url.startsWith('https://'), {
    message: 'URL must use HTTPS',
  }),
  // Context fields (all optional)
  platform: z.string().optional(),
  placement_type: z.string().optional(),
  post_url: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  author_handle: z.string().optional(),
  author: z.string().optional(),
  excerpt: z.string().optional(),
  // Legacy context field for backward compatibility
  context: z.record(z.unknown()).optional(),
});

export type PlacementFormData = z.infer<typeof placementSchema>;

export interface PlacementFormProps {
  domains: DomainResponse[];
  onSubmit: (data: PlacementFormData) => Promise<void>;
  onCancel?: () => void;
  defaultValues?: Partial<PlacementFormData>;
  isLoading?: boolean;
  error?: string | null;
}

export function PlacementForm({
  domains,
  onSubmit,
  onCancel,
  defaultValues,
  isLoading = false,
  error,
}: PlacementFormProps) {
  // Extract context fields from defaultValues.context if present
  const contextDefaults = defaultValues?.context as Record<string, unknown> | undefined;
  const computedDefaultValues: Partial<PlacementFormData> = {
    ...defaultValues,
    ...(domains.length === 1 && !defaultValues?.domain_id
      ? { domain_id: domains[0].domain_id }
      : {}),
    // Extract context fields if context object exists
    ...(contextDefaults
      ? {
          platform: (contextDefaults.platform as string) || '',
          placement_type: (contextDefaults.placement_type as string) || '',
          post_url: (contextDefaults.post_url as string) || '',
          author_handle: (contextDefaults.author_handle as string) || '',
          author: (contextDefaults.author as string) || '',
          excerpt: (contextDefaults.excerpt as string) || (contextDefaults.context_excerpt as string) || '',
        }
      : {}),
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm<PlacementFormData>({
    resolver: zodResolver(placementSchema),
    defaultValues: computedDefaultValues,
  });

  // Transform form data to include context object before submission
  const handleFormSubmit = async (data: PlacementFormData) => {
    // Build context object from form fields (only include non-empty values)
    const context: Record<string, unknown> = {};
    if (data.platform && data.platform.trim()) context.platform = data.platform.trim();
    if (data.placement_type && data.placement_type.trim()) context.placement_type = data.placement_type.trim();
    if (data.post_url && data.post_url.trim()) context.post_url = data.post_url.trim();
    if (data.author_handle && data.author_handle.trim()) context.author_handle = data.author_handle.trim();
    if (data.author && data.author.trim()) context.author = data.author.trim();
    if (data.excerpt && data.excerpt.trim()) context.excerpt = data.excerpt.trim();

    // Submit with context object (only if it has values)
    await onSubmit({
      domain_id: data.domain_id,
      destination_url: data.destination_url,
      context: Object.keys(context).length > 0 ? context : undefined,
    });
  };

  // Set domain value when domains change and only one exists
  useEffect(() => {
    if (domains.length === 1) {
      const currentDomainId = getValues('domain_id');
      if (!currentDomainId || currentDomainId === '') {
        setValue('domain_id', domains[0].domain_id, { shouldValidate: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domains.length]);

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="placement-form">
      {error && (
        <div className="placement-form__error-message" style={{ 
          padding: 'var(--of-space-3)', 
          marginBottom: 'var(--of-space-4)', 
          backgroundColor: 'var(--of-color-error-light, #fee)', 
          color: 'var(--of-color-error, #c33)', 
          borderRadius: 'var(--of-radius-sm, 4px)',
          border: '1px solid var(--of-color-error, #c33)'
        }}>
          {error}
        </div>
      )}
      <div className="placement-form__field">
        <label htmlFor="domain_id" className="label">
          Domain
        </label>
        <select
          id="domain_id"
          {...register('domain_id')}
          className="select"
          disabled={isLoading}
        >
          <option value="">Select a domain</option>
          {domains.map((domain) => (
            <option key={domain.domain_id} value={domain.domain_id}>
              {domain.domain_name}
            </option>
          ))}
        </select>
        {errors.domain_id && (
          <p className="placement-form__error">{errors.domain_id.message}</p>
        )}
      </div>

      <div className="placement-form__field">
        <label htmlFor="destination_url" className="label">
          Destination URL
        </label>
        <input
          id="destination_url"
          type="url"
          {...register('destination_url')}
          className="input"
          placeholder="https://example.com"
          disabled={isLoading}
        />
        <p className="placement-form__help-text">
          Where users will be redirected when they click your tracking link
        </p>
        {errors.destination_url && (
          <p className="placement-form__error">{errors.destination_url.message}</p>
        )}
      </div>

      {/* Context Metadata Section */}
      <div className="placement-form__section">
        <h3 className="h4" style={{ marginBottom: 'var(--of-space-4)', marginTop: 'var(--of-space-6)' }}>
          Placement Context (Optional)
        </h3>
        <p className="placement-form__help-text" style={{ marginBottom: 'var(--of-space-4)' }}>
          Add metadata about where this link will be placed (e.g., social media post, blog article)
        </p>

        <div className="placement-form__field">
          <label htmlFor="platform" className="label">
            Platform
          </label>
          <select
            id="platform"
            {...register('platform')}
            className="select"
            disabled={isLoading}
          >
            <option value="">Select platform</option>
            <option value="facebook.com">Facebook</option>
            <option value="x.com">X (Twitter)</option>
            <option value="linkedin.com">LinkedIn</option>
            <option value="reddit.com">Reddit</option>
            <option value="youtube.com">YouTube</option>
            <option value="instagram.com">Instagram</option>
            <option value="tiktok.com">TikTok</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className="placement-form__field">
          <label htmlFor="placement_type" className="label">
            Placement Type
          </label>
          <select
            id="placement_type"
            {...register('placement_type')}
            className="select"
            disabled={isLoading}
          >
            <option value="">Select type</option>
            <option value="post">Post</option>
            <option value="comment">Comment</option>
            <option value="article">Article</option>
            <option value="email">Email</option>
            <option value="ad">Ad</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className="placement-form__field">
          <label htmlFor="post_url" className="label">
            Post/Content URL
          </label>
          <input
            id="post_url"
            type="url"
            {...register('post_url')}
            className="input"
            placeholder="https://facebook.com/user/posts/12345"
            disabled={isLoading}
          />
          <p className="placement-form__help-text">
            URL of the post/article where this link will be placed (can be added later)
          </p>
          {errors.post_url && (
            <p className="placement-form__error">{errors.post_url.message}</p>
          )}
        </div>

        <div className="placement-form__field">
          <label htmlFor="author_handle" className="label">
            Author Handle
          </label>
          <input
            id="author_handle"
            type="text"
            {...register('author_handle')}
            className="input"
            placeholder="@username or u/username"
            disabled={isLoading}
          />
        </div>

        <div className="placement-form__field">
          <label htmlFor="author" className="label">
            Author Name
          </label>
          <input
            id="author"
            type="text"
            {...register('author')}
            className="input"
            placeholder="John Doe"
            disabled={isLoading}
          />
        </div>

        <div className="placement-form__field">
          <label htmlFor="excerpt" className="label">
            Excerpt/Notes
          </label>
          <textarea
            id="excerpt"
            {...register('excerpt')}
            className="input"
            placeholder="Brief description or excerpt from the post..."
            rows={3}
            disabled={isLoading}
          />
        </div>
      </div>

      <div className="placement-form__actions">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Creating...' : 'Create Placement'}
        </Button>
      </div>
    </form>
  );
}

